import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../translations/i18n';
import apiClient from './api-client';
import { setIsBusy } from './app';
import { MESSAGE_DIALOG_BUTTONS, MESSAGE_DIALOG_TYPE, showMessageDialogAsync } from './message-dialog';
import { showToast, showToastWithApiClientErrorInfo, TOAST_TYPE } from './toast';

// eslint-disable-next-line no-unused-vars
let _getState;
const registerGetStateInInvoicesSlice = (storeGetState) => {
  _getState = storeGetState;
};

const slice = createSlice({
  name: 'invoices',
  initialState: {
    selectedId: null,
    selectedNo: null,
    selectedEmailSent: false,
    idDictionary: [],
    contractorId: null,
    allRowsCount: 0,
    sortInfo: null,
    summary: { grossValue: 0, taxValue: 0, netValue: 0 },
    nseiInvoiceInfo: null,
    nseiLighthouseStatus: null,
  },
  reducers: {
    setSortInfo: (state, action) => {
      const stateRef = state;
      stateRef.sortInfo = action.payload;
    },
    setSelectedId: (state, action) => {
      const stateRef = state;
      stateRef.selectedId = action.payload;
    },
    setSelectedNo: (state, action) => {
      const stateRef = state;
      stateRef.selectedNo = action.payload;
    },
    setSelectedEmailSent: (state, action) => {
      const stateRef = state;
      stateRef.selectedEmailSent = action.payload;
    },
    setContractorId: (state, action) => {
      const stateRef = state;
      stateRef.contractorId = action.payload;
    },
    setAllRowsCount: (state, action) => {
      const stateRef = state;
      stateRef.allRowsCount = action.payload;
    },
    setSummary: (state, action) => {
      const stateRef = state;
      stateRef.summary = action.payload;
    },
    reset: (state) => {
      const stateRef = state;
      stateRef.selectedId = null;
      stateRef.selectedEmailSent = false;
      stateRef.idDictionary = [];
      stateRef.contractorId = null;
      stateRef.allRowsCount = 0;
      stateRef.sortInfo = null;
      stateRef.summary = { grossValue: 0, taxValue: 0, netValue: 0 };
    },
    setNSEIInvoiceInfo: (state, action) => {
      const stateRef = state;
      stateRef.nseiInvoiceInfo = action.payload;
    },
    setNSEILighthouseStatus: (state, action) => {
      const stateRef = state;
      stateRef.nseiLighthouseStatus = action.payload;
    },
  },
});

const {
  setNSEIInvoiceInfo,
  setNSEILighthouseStatus,
  setSelectedId,
  setSortInfo,
  setContractorId,
  setAllRowsCount,
  setSummary,
  setSelectedNo,
  setSelectedEmailSent,
  reset,
} = slice.actions;

const selectIdDictionary = (state) => state.invoices.idDictionary;
const selectSelectedId = (state) => state.invoices.selectedId;
const selectSelectedNo = (state) => state.invoices.selectedNo;
const selectSelectedEmailSent = (state) => state.invoices.selectedEmailSent === true;
const selectContractorId = (state) => state.invoices.contractorId;
const selectLoadedDataExists = (state) => state.invoices.allRowsCount > 0;
const selectAllRowsCount = (state) => state.invoices.allRowsCount;
const selectSortInfo = (state) => state.invoices.sortInfo;
const selectSummary = (state) => state.invoices.summary;
const selectNSEIInvoiceInfo = (state) => state.invoices.nseiInvoiceInfo;
const selectNSEILighthouseStatus = (state) => state.invoices.nseiLighthouseStatus;

const NSEI_AUTO_MAX_ATTEMPTS = 12;
const NSEI_AUTO_POLL_MS = 3000;

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const parseInvoiceNseiData = (invoiceNseiData) => {
  if (invoiceNseiData == null) {
    return null;
  }
  if (typeof invoiceNseiData === 'string') {
    try {
      const parsedData = JSON.parse(invoiceNseiData);
      if (parsedData.invoiceStatusDetails != null && typeof parsedData.invoiceStatusDetails === 'string') {
        return { ...parsedData, invoiceStatusDetails: JSON.parse(parsedData.invoiceStatusDetails) };
      }
      return parsedData;
    } catch {
      return null;
    }
  }
  return invoiceNseiData;
};

const isInvoiceInKsef = (invoiceNseiStatus, parsedInvoiceNseiData) =>
  invoiceNseiStatus === 0 && parsedInvoiceNseiData?.invoiceStatus === 200;

const fetchNSEIInfo = async (invoiceId) => {
  const response = await apiClient.get(`/invoices/${invoiceId}/nsei`);
  return {
    invoiceNseiStatus: response.data.invoiceNseiStatus,
    parsedInvoiceNseiData: parseInvoiceNseiData(response.data.invoiceNseiData),
  };
};

const loadData =
  (dispatch) =>
  ({ contractorId, filter: filterValue }) =>
  ({ skip, limit, sortInfo }) =>
    new Promise((resolve, reject) => {
      (async () => {
        try {
          dispatch(setIsBusy(true));

          const response = await apiClient.get(
            contractorId == null ? '/invoices' : `/contractors/${contractorId}/invoices`,
            {
              params: { limit, offset: skip, sortInfo, filterValue: { filterValue } },
            },
          );

          const { count, summary } = response.data.invoices;
          dispatch(setSummary(summary));
          dispatch(setAllRowsCount(count));
          dispatch(setSortInfo(sortInfo));
          dispatch(setContractorId(contractorId));
          dispatch(setSelectedId(null));
          dispatch(setIsBusy(false));

          const parseInvoiceNseiData = (invoiceNseiData) => {
            if (invoiceNseiData == null) {
              return null;
            }
            try {
              const parsedData = JSON.parse(invoiceNseiData);
              const result = { ...parsedData, invoiceStatusDetails: JSON.parse(parsedData.invoiceStatusDetails) };
              return result;
            } catch {
              return null;
            }
          };

          resolve({
            data: response.data.invoices.rows.map((invoice, index) => ({
              ...invoice,
              parsedInvoiceNseiData: parseInvoiceNseiData(invoice.invoiceNseiData),
              index: index + 1,
              grossValue: invoice.grossValue.toFixed(2),
              netValue: invoice.netValue.toFixed(2),
              taxValue: invoice.taxValue.toFixed(2),
            })),
            count,
          });
        } catch (error) {
          dispatch(setIsBusy(false));
          reject(error);
        }
      })();
    });

const openInvoiceAsync =
  ({ invoiceId, contractorId }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const invoicesUri =
        contractorId == null ? `/invoices/${invoiceId}/pdf` : `/contractors/${contractorId}/invoices/${invoiceId}/pdf`;
      const response = await apiClient.get(invoicesUri, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = URL.createObjectURL(blob);
      const w = window.open(fileUrl, '_blank');
      w?.focus();
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Open invoice error', error });
    }
    dispatch(setIsBusy(false));
  };
const openNseiUpoAsync =
  ({ invoiceId, format, onSuccess }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const invoicesUri = `/invoices/${invoiceId}/nsei/upo`;
      const response = await apiClient.get(invoicesUri, { responseType: 'blob', params: { format } });
      const blob = new Blob([response.data], { type: format === 'xml' ? 'application/xml' : 'application/pdf' });
      const fileUrl = URL.createObjectURL(blob);
      const w = window.open(fileUrl, '_blank');
      w?.focus();
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Download receipt error', error });
    }
    if (onSuccess != null) {
      onSuccess();
    }
    dispatch(setIsBusy(false));
  };

const openNseiInvoiceAsync =
  ({ invoiceId, format, sendByEmail, onSuccess }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const invoicesUri = `/invoices/${invoiceId}/nsei/invoice`;
      const response = await apiClient.get(invoicesUri, { responseType: 'blob', params: { format, sendByEmail } });
      if (!sendByEmail) {
        const blob = new Blob([response.data], { type: format === 'xml' ? 'application/xml' : 'application/pdf' });
        const fileUrl = URL.createObjectURL(blob);
        const w = window.open(fileUrl, '_blank');
        w?.focus();
      }
    } catch (error) {
      if (sendByEmail) {
        showToastWithApiClientErrorInfo({ title: 'Send invoice by email error', error });
      } else {
        showToastWithApiClientErrorInfo({ title: 'Download invoice error', error });
      }
    }
    if (onSuccess != null) {
      onSuccess();
    }
    dispatch(setIsBusy(false));
  };

const handleNSEIInvoiceAsync =
  ({ invoiceId, onSuccess }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const invoicesUri = `/invoices/${invoiceId}/nsei`;
      await apiClient.post(invoicesUri);
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Invoice NSEI error', error });
    }
    if (onSuccess != null) {
      onSuccess();
    }
    dispatch(setIsBusy(false));
  };

const handleNSEIInvoiceRefreshStatusAsync =
  ({ invoiceId, onSuccess }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const invoicesUri = `/invoices/${invoiceId}/nsei/refresh-status`;
      await apiClient.get(invoicesUri);
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Invoice NSEI error', error });
    }
    if (onSuccess != null) {
      onSuccess();
    }
    dispatch(setIsBusy(false));
  };

const loadNSEIInvoiceInfoAsync =
  ({ invoiceId }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const invoicesUri = `/invoices/${invoiceId}/nsei`;
      const response = await apiClient.get(invoicesUri);
      const parsedInvoiceNseiData = parseInvoiceNseiData(response.data.invoiceNseiData);

      dispatch(
        setNSEIInvoiceInfo({
          parsedInvoiceNseiData,
          invoiceNseiData: response.data.invoiceNseiData,
          invoiceNseiStatus: response.data.invoiceNseiStatus,
        }),
      );
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Invoice NSEI Info error', error });
    }
    dispatch(setIsBusy(false));
  };

const processNSEIInvoiceAutoAsync =
  ({ invoiceId, isCorrectionInvoice, onSuccess }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    let shouldReload = false;
    try {
      showToast({
        title: i18n.t('features.invoices.NSEIAutoStarted', 'NSEI auto process'),
        description: i18n.t(
          'features.invoices.NSEIAutoStartedDescription',
          'Sending invoice to KSeF and then by email…',
        ),
        type: TOAST_TYPE.INFO,
        duration: 5000,
      });

      const lighthouseResponse = await apiClient.get('/nsei/lighthouse/status');
      dispatch(setNSEILighthouseStatus(lighthouseResponse.data));
      if (lighthouseResponse.data?.status !== 'AVAILABLE') {
        showToast({
          title: i18n.t('features.invoices.NSEIAutoForbidden', 'Operation forbidden'),
          description: i18n.t(
            'features.invoices.NSEIAutoLighthouseUnavailable',
            'KSeF lighthouse status is not AVAILABLE',
          ),
          type: TOAST_TYPE.ERROR,
          duration: 10000,
        });
        return;
      }

      if (isCorrectionInvoice) {
        const invoiceResponse = await apiClient.get(`/invoices/${invoiceId}`);
        const correctedInvoiceId = invoiceResponse.data?.invoice_id;
        if (correctedInvoiceId != null) {
          const correctedNsei = await fetchNSEIInfo(correctedInvoiceId);
          if (!isInvoiceInKsef(correctedNsei.invoiceNseiStatus, correctedNsei.parsedInvoiceNseiData)) {
            showToast({
              title: i18n.t('features.invoices.NSEIAutoForbidden', 'Operation forbidden'),
              description: i18n.t(
                'features.invoices.NSEIAutoCorrectionBlocked',
                'Correction can be sent only when the original invoice is already in KSeF',
              ),
              type: TOAST_TYPE.ERROR,
              duration: 10000,
            });
            return;
          }
        }
      }

      await apiClient.post(`/invoices/${invoiceId}/nsei`);
      shouldReload = true;

      let info = await fetchNSEIInfo(invoiceId);
      if (info.invoiceNseiStatus >= 1) {
        showToast({
          title: i18n.t('features.invoices.NSEIAutoSendFailed', 'NSEI send failed'),
          description: i18n.t(
            'features.invoices.NSEIAutoSendFailedDescription',
            'Invoice was not registered in KSeF. Leave it for operator review.',
          ),
          type: TOAST_TYPE.ERROR,
          duration: 10000,
        });
        return;
      }

      if (!isInvoiceInKsef(info.invoiceNseiStatus, info.parsedInvoiceNseiData)) {
        let registered = false;
        for (let attempt = 0; attempt < NSEI_AUTO_MAX_ATTEMPTS; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(NSEI_AUTO_POLL_MS);
          // eslint-disable-next-line no-await-in-loop
          await apiClient.get(`/invoices/${invoiceId}/nsei/refresh-status`);
          // eslint-disable-next-line no-await-in-loop
          info = await fetchNSEIInfo(invoiceId);

          if (info.invoiceNseiStatus >= 1) {
            showToast({
              title: i18n.t('features.invoices.NSEIAutoSendFailed', 'NSEI send failed'),
              description: i18n.t(
                'features.invoices.NSEIAutoSendFailedDescription',
                'Invoice was not registered in KSeF. Leave it for operator review.',
              ),
              type: TOAST_TYPE.ERROR,
              duration: 10000,
            });
            return;
          }

          if (isInvoiceInKsef(info.invoiceNseiStatus, info.parsedInvoiceNseiData)) {
            registered = true;
            break;
          }
        }

        if (!registered) {
          showToast({
            title: i18n.t('features.invoices.NSEIAutoStillProcessing', 'NSEI still processing'),
            description: i18n.t(
              'features.invoices.NSEIAutoStillProcessingDescription',
              'Invoice is still being processed in KSeF. Refresh status manually later.',
            ),
            type: TOAST_TYPE.WARNING,
            duration: 10000,
          });
          return;
        }
      }

      await apiClient.get(`/invoices/${invoiceId}/nsei/invoice`, {
        responseType: 'blob',
        params: { format: 'pdf', sendByEmail: true },
      });

      showToast({
        title: i18n.t('features.invoices.NSEIAutoSucceed', 'NSEI auto process completed'),
        description: i18n.t(
          'features.invoices.NSEIAutoSucceedDescription',
          'Invoice registered in KSeF and sent by email.',
        ),
        type: TOAST_TYPE.SUCCESS,
        duration: 10000,
      });
    } catch (error) {
      shouldReload = true;
      showToastWithApiClientErrorInfo({ title: 'NSEI auto process error', error });
    } finally {
      dispatch(setIsBusy(false));
      if (onSuccess != null) {
        onSuccess({ shouldReload });
      }
    }
  };

const loadNSEILighthouseStatusAsync = () => async (dispatch) => {
  dispatch(setIsBusy(true));
  try {
    const lighthouseResponse = await apiClient.get('/nsei/lighthouse/status');
    dispatch(setNSEILighthouseStatus(lighthouseResponse.data));
  } catch (error) {
    showToastWithApiClientErrorInfo({ title: 'Lighthouse status error', error });
  }
  dispatch(setIsBusy(false));
};

const openInvoicesReportAsync =
  ({ contractorId, filter: filterValue, sortInfo }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const response = await apiClient.get(
        contractorId == null ? '/invoices/pdf' : `/contractors/${contractorId}/invoices/pdf`,
        {
          responseType: 'blob',
          params: { sortInfo, filterValue: { filterValue } },
        },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = URL.createObjectURL(blob);
      const w = window.open(fileUrl, '_blank');
      w?.focus();
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Open invoices report error', error });
    }
    dispatch(setIsBusy(false));
  };

const deleteLastInvoiceAsync =
  ({ reload, setReload }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const response = await apiClient.get('/invoices/last/info');
      const nseiResponse = await apiClient.get(`/invoices/${response.data.id}/nsei`);
      if (nseiResponse.data.invoiceNseiStatus != null) {
        showToast({
          title: i18n.t('features.invoices.InvoiceDeleteFailed', 'Invoice delete failed'),
          description: i18n.t(
            'features.invoices.InvoiceDeleteFailedDescription',
            'Invoice was sent to NSEI and cannot be deleted',
          ),
          type: TOAST_TYPE.INFO,
          duration: 10000,
        });
        return;
      }
      dispatch(
        showMessageDialogAsync({
          type: MESSAGE_DIALOG_TYPE.CONFIRMATION,
          buttons: MESSAGE_DIALOG_BUTTONS.YES_NO,
          text: `Faktura nr ${response.data.invoice_no} wystawiona na kontrahenta: ${response.data.contractor_name} (NIP: ${response.data.contractor_vatin}) zostanie bezpowrotnie usunięta z systemu. Kontynuować?`,
          actions: {
            yes: async () => {
              try {
                // eslint-disable-next-line no-unused-vars
                // const _deleteResponse = await apiClient.delete(`/invoices/${response.data.id}`);
                showToast({
                  title: i18n.t('features.invoices.InvoiceDeleteSucceed', 'Invoice delete succeed'),
                  description: `${i18n.t(
                    'features.invoices.InvoiceDeleteSucceedDescription',
                    'Invoice delete succeed. Deleted invoice no: ',
                  )}${response.data.invoice_no}`,
                  type: TOAST_TYPE.SUCCESS,
                  duration: 10000,
                });
              } catch (error) {
                showToastWithApiClientErrorInfo({ title: 'Delete last invoice error', error });
              }

              if (setReload != null) {
                setReload(reload + 1);
              }
            },
          },
        }),
      );
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Delete last invoice error', error });
    }
    dispatch(setIsBusy(false));
  };

const createInvoiceCorrectionAsync =
  ({ correctedInvoiceId, correctionReason, vatin, name, address1, address2, amount, paymentMethod, description, reload, setReload, onSuccess }) =>
  async (dispatch) => {
    dispatch(setIsBusy(true));
    try {
      const response = await apiClient.post(`/invoices/${correctedInvoiceId}/correction`, {
        correctedInvoiceId,
        correctionReason,
        vatin,
        name,
        address1,
        address2,
        amount,
        paymentMethod,
        description,
      });
      showToast({
        title: i18n.t('features.invoices.InvoiceCorrectionSucceed', 'Invoice correction created'),
        description: `${i18n.t(
          'features.invoices.InvoiceCorrectionSucceedDescription',
          'Correction invoice no: ',
        )}${response.data.invoiceNo}`,
        type: TOAST_TYPE.SUCCESS,
        duration: 10000,
      });
      if (setReload != null) {
        setReload(reload + 1);
      }
      if (onSuccess != null) {
        onSuccess(response.data);
      }
    } catch (error) {
      showToastWithApiClientErrorInfo({ title: 'Create invoice correction error', error });
    }
    dispatch(setIsBusy(false));
  };

export {
  registerGetStateInInvoicesSlice,
  slice,
  setSelectedId,
  setSelectedNo,
  setSelectedEmailSent,
  reset,
  selectSortInfo,
  selectSelectedId,
  selectSelectedNo,
  selectSelectedEmailSent,
  selectIdDictionary,
  selectContractorId,
  selectLoadedDataExists,
  selectAllRowsCount,
  selectSummary,
  loadData,
  openNseiUpoAsync,
  openNseiInvoiceAsync,
  openInvoicesReportAsync,
  deleteLastInvoiceAsync,
  createInvoiceCorrectionAsync,
  handleNSEIInvoiceAsync,
  loadNSEIInvoiceInfoAsync,
  processNSEIInvoiceAutoAsync,
  selectNSEIInvoiceInfo,
  handleNSEIInvoiceRefreshStatusAsync,
  setNSEIInvoiceInfo,
  openInvoiceAsync,
  selectNSEILighthouseStatus,
  loadNSEILighthouseStatusAsync,
};
export default slice.reducer;
